import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, jsonb, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { user } from '../../auth'

// =============================================================================
// READING METRICS DAILY - Lookup table (closed UTC days; today is computed live)
// Stores aggregated reading metrics data by user and date
// Used to calculate child profile metrics (30-day and 90-day aggregations)
//
// DEBUGGING NOTES (read before touching this table or its data):
// - Formula: `computeReadingMetricsDaily` (packages/trigger/src/metrics/reading-metrics-daily.ts),
//   a pure read over ReadingSession + ReadingSessionSummary, absolute per (userId, UTC day).
//   `rollupUsers` stores it for closed days (nightly cron, recomputePastDays, core / rollup-users)
//   by deleting the (users, range) slice and re-inserting it; the API runs the same function for
//   today. No `calculatedFor*` flag is read or written.
// - Reset/recompute: `reset-org-lookup-tables` (packages/trigger/src/trigger/core/
//   reset-org-lookup-tables.ts) deletes the org's children's rows by userId and rebuilds them.
// - Grain: exactly one row per (userId, date) — daily aggregate, not per-session.
// =============================================================================

export const ReadingMetricsDaily = pgTable(
  'reading_metrics_daily',
  {
    id: uuid().defaultRandom().primaryKey(), // Surrogate PK; NOT the upsert key (see unique constraint below).
    userId: text()
      .references(() => user.id, { onDelete: 'cascade' }) // FK to user; row is removed automatically when the user is deleted.
      .notNull(),
    // The specific date (normalized to START-OF-DAY UTC by the producer's getStartOfDayUTC()).
    // Always midnight UTC — never a local-tz day boundary. Half of the (userId, date) upsert key.
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // The specific date (normalized to start of day)

    // Daily aggregated metrics
    // UNIT: SECONDS (not minutes). Sum of ReadingSessionSummary.totalDurationSeconds across this day's sessions.
    // Used as the denominator when converting the *Distribution maps from seconds to percentages.
    totalReadingSeconds: integer().notNull().default(0), // Sum of all session durations for this day
    // UNIT: count. Number of ReadingSessions anchored on this day, recomputed absolutely from the day's
    // full session set on every roll-up.
    sessionCount: integer().notNull().default(0), // Number of reading sessions on this day
    // UNIT: count of distinct bookIds with >=1 session that day — an exact `count(distinct)` over the
    // day's full session set.
    uniqueBooksRead: integer().notNull().default(0), // Number of unique books that had at least one session this day
    // UNIT: count of distinct books with a completed session that day (ReadingSessionSummary.isCompleted),
    // same exact recount as uniqueBooksRead.
    completedBooksCount: integer().notNull().default(0), // Number of books completed on this day

    // Category distributions (stored as JSONB)
    // Format: { "categoryId": percentage } or { "categoryId": count }
    // IMPORTANT (current producer behavior): values are reading-SECONDS expressed as a PERCENTAGE of
    // totalReadingSeconds for the day (i.e. share-of-time per category), NOT book counts and NOT raw seconds.
    // Producer stores seconds internally, then divides by totalReadingSeconds * 100 on write. Percentages
    // across a single map sum to ~100 (subject to float rounding); they will NOT sum to 100 if some sessions
    // had no category mapping. The legacy "count" wording above no longer reflects what is written.
    // Reading Level (Jenjang Baca) distribution - maps BookLevel.id to percentage/count
    // Key = Book.bookLevelId. Value = % of the day's reading-seconds spent at that level.
    readingLevelDistribution: jsonb('reading_level_distribution'),
    // Theme (Tema) distribution - maps BookTheme.id to percentage/count
    // Key = BookTheme.id (book→theme is many-to-many). GOTCHA: when a book has N themes the producer splits
    // that session's seconds EVENLY across all N themes (duration / N) before computing percentages.
    themeDistribution: jsonb('theme_distribution'),
    // Subject (Mata Pelajaran) distribution - maps Subject.id to percentage/count
    // Key = Subject.id resolved via Lesson.subjectId; producer uses the FIRST subject if a book has multiple lessons.
    subjectDistribution: jsonb('subject_distribution'),

    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // Because there is no FK, an id on its own is unrenderable once the row it pointed at is gone,
    // so the labels are stored beside it. They are stamped at roll-up time like everything else
    // here, which means a rebuild re-stamps them too — a renamed class rewrites its own history.
    schoolOrganizationId: text(),
    // The school's name at roll-up time, mirroring organization.name.
    schoolOrganizationName: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The class label a report prints ("1 A"), mirroring team.name.
    schoolTeamName: text(),
    // The class avatar URL, mirroring team.image.
    schoolTeamImage: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),
    // The learner's own name and avatar at roll-up time, mirroring user.name / user.image.
    // Snapshotted for the same reason as the class label: a learner can be renamed or deleted.
    studentName: text(),
    studentImage: text(),

    // Tracking fields
    // Set by rollupUsers to its run time on every write (NOT db-side) — use to tell when a row was last recomputed.
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // Row insert time (db default now()).
    // Auto-bumped to now() on UPDATE. Nullable: stays NULL on the initial INSERT, only set once the row is updated.
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    // Single-column index for per-user lookups (e.g. profile aggregation, reset-by-user deletes).
    index('idx_reading_metrics_daily_user').on(t.userId),
    // Single-column index for date-range scans (30-day / 90-day windows).
    index('idx_reading_metrics_daily_date').on(t.date),
    // Composite index backing the common "this user, this date range" access pattern.
    index('idx_reading_metrics_daily_user_date').on(t.userId, t.date),
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_reading_metrics_daily_school_org_date').on(t.schoolOrganizationId, t.date),
    index('idx_reading_metrics_daily_school_team_date').on(t.schoolTeamId, t.date),
    // UNIQUE (userId, date): enforces one row per user per day. The store deletes the (users, range) slice
    // before inserting, so a duplicate-key error here means a compute emitted two rows for one day.
    unique('uq_reading_metrics_daily_user_date').on(t.userId, t.date),
  ],
)

export const readingMetricsDailyRelations = relations(ReadingMetricsDaily, ({ one }) => ({
  user: one(user, {
    fields: [ReadingMetricsDaily.userId],
    references: [user.id],
  }),
}))

// Insert payload schema (drizzle-zod). Omits id (auto uuid) and createdAt/updatedAt (db-managed timestamps),
// so the producer only supplies userId/date/metrics/distributions/calculatedAt.
export const InsertReadingMetricsDailySchema = createInsertSchema(ReadingMetricsDaily).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

// Update payload schema (drizzle-zod). .partial() => every field optional. Omits the immutable identity
// columns (id, userId, date — the upsert key) plus db-managed createdAt/updatedAt, so updates can only touch
// the metric/distribution/calculatedAt fields.
export const UpdateReadingMetricsDailySchema = createUpdateSchema(ReadingMetricsDaily).partial().omit({
  id: true,
  userId: true,
  date: true,
  createdAt: true,
  updatedAt: true,
})

// Select/read schema (drizzle-zod) — full row, nothing omitted. Note distributions type as jsonb (unknown shape):
// validate/cast them to Record<string, number> at read sites if you need the percentage maps typed.
export const SelectReadingMetricsDailySchema = createSelectSchema(ReadingMetricsDaily)

export type InsertReadingMetricsDaily = typeof ReadingMetricsDaily.$inferInsert
export type UpdateReadingMetricsDaily = z.infer<typeof UpdateReadingMetricsDailySchema>
export type SelectReadingMetricsDaily = typeof ReadingMetricsDaily.$inferSelect
